import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  createChildColor,
  getAllChildColors,
  updateChildColor,
  deleteChildColor,
  clearErrors as clearChildColorErrors,
} from "../../redux/actions/childColorActions";

import {
  getAllParentColors,
  clearErrors as clearParentColorErrors,
} from "../../redux/actions/parentColor";

import "./Colors.scss";

const Colors = () => {
  const dispatch = useDispatch();

  // --- Дані з childColor-ред’юсера ---
  const {
    isLoading: childLoading,
    error: childError,
    childColors = [],
    success: childSuccess,
    message: childMessage,
  } = useSelector((state) => state.childcolor);

  // --- Дані з parentColor-ред’юсера (щоб вибирати батьківські кольори) ---
  const {
    colors: parentColors = [],
    error: parentError,
  } = useSelector((state) => state.parentColor);

  // --- СТЕЙТ ДЛЯ СТВОРЕННЯ НОВОГО ДИТЯЧОГО КОЛЬОРУ ---
  const [name, setName] = useState("");
  const [parents, setParents] = useState([]); // масив _id батьківських кольорів
  const [images, setImages] = useState([]); // base64 або url

  // --- СТЕЙТ ДЛЯ РЕДАГУВАННЯ ---
  const [editMode, setEditMode] = useState(false);
  const [editColorId, setEditColorId] = useState(null);

  const [editName, setEditName] = useState("");
  const [editParents, setEditParents] = useState([]); // масив _id
  const [editImagesToDelete, setEditImagesToDelete] = useState([]); // public_id
  const [newImages, setNewImages] = useState([]); // base64

  // --- 1. Завантажуємо батьківські кольори та дочірні кольори ---
  useEffect(() => {
    dispatch(getAllParentColors());
    dispatch(getAllChildColors());
  }, [dispatch]);

  // --- 2. Обробка помилок та повідомлень (childColor) ---
  useEffect(() => {
    if (childError) {
      alert(childError);
      dispatch(clearChildColorErrors());
    }
    if (childMessage) {
      alert(childMessage);
    }
    // Якщо операція (CREATE/UPDATE/DELETE) була успішною —
    // оновлюємо список кольорів заново
    if (childSuccess) {
      dispatch(getAllChildColors());
    }
  }, [childError, childMessage, childSuccess, dispatch]);

  // --- 3. Обробка помилок від батьківських кольорів (опційно) ---
  useEffect(() => {
    if (parentError) {
      alert(parentError);
      dispatch(clearParentColorErrors());
    }
  }, [parentError, dispatch]);

  // ------------------ СТВОРЕННЯ ДИТЯЧОГО КОЛЬОРУ ------------------ //
  const handleCreateChildColor = () => {
    if (!name.trim()) {
      return alert("Вкажіть назву дитячого кольору!");
    }
    if (parents.length === 0) {
      return alert("Оберіть принаймні один батьківський колір!");
    }

    dispatch(createChildColor(name, parents, images));

    // Очистити локальний стейт
    setName("");
    setParents([]);
    setImages([]);
  };

  // ------------------ ВИДАЛЕННЯ ДИТЯЧОГО КОЛЬОРУ ------------------ //
  const handleDeleteChildColor = (id) => {
    if (window.confirm("Ви впевнені, що хочете видалити цей колір?")) {
      dispatch(deleteChildColor(id));
    }
  };

  // ------------------ ВХІДНІ ФАЙЛИ (BASE64) ------------------ //
  // isNewImages = false => завантажуємо у поточну групу (для створення)
  // isNewImages = true => завантажуємо у newImages (для редагування)
  const handleFileChange = (e, isNewImages = false) => {
    const files = Array.from(e.target.files);

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.readyState === 2) {
          if (isNewImages) {
            setNewImages((prev) => [...prev, reader.result]);
          } else {
            setImages((prev) => [...prev, reader.result]);
          }
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // ------------------ УВІМКНЕННЯ РЕЖИМУ РЕДАГУВАННЯ ------------------ //
  const handleEditMode = (childColor) => {
    setEditMode(true);
    setEditColorId(childColor._id);

    // Встановлюємо початкові дані
    setEditName(childColor.name || "");
    // Можливо, ваш бекенд повертає parents як масив об’єктів, тому беремо саме їх _id
    const parentIds =
      childColor.parents?.map((p) => (typeof p === "string" ? p : p._id)) || [];
    setEditParents(parentIds);
    setEditImagesToDelete([]);
    setNewImages([]);
  };

  // ------------------ ВИХІД З РЕЖИМУ РЕДАГУВАННЯ ------------------ //
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditColorId(null);
    setEditName("");
    setEditParents([]);
    setEditImagesToDelete([]);
    setNewImages([]);
  };

  // ------------------ ОНОВЛЕННЯ ДИТЯЧОГО КОЛЬОРУ ------------------ //
  const handleUpdateChildColor = () => {
    if (!editColorId) return;

    const updatedData = {
      name: editName,
      parents: editParents, // масив _id
      imagesToDelete: editImagesToDelete, // public_id
      newImages, // base64
    };

    dispatch(updateChildColor(editColorId, updatedData));

    // Виходимо з режиму редагування
    setEditMode(false);
    setEditColorId(null);
    setEditName("");
    setEditParents([]);
    setEditImagesToDelete([]);
    setNewImages([]);
  };

  // ------------------ ДОДАТИ зображення у список на видалення ------------------ //
  const handleMarkForDelete = (public_id) => {
    setEditImagesToDelete((prev) => [...prev, public_id]);
  };

  // ------------------ ВИДАЛИТИ "нове" зображення (з локального стейту) ------------------ //
  const removeNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, idx) => idx !== index));
  };

  // ------------------ Вибір батьківського кольору (MULIPLE SELECT) ------------------ //
  // Припустимо, що для створення/редагування ви можете обирати кілька батьківських кольорів:
  const handleSelectParents = (e, isEditMode = false) => {
    // <select multiple>, тому отримуємо масив вибраних options
    const selectedOptions = Array.from(e.target.selectedOptions);
    const selectedIds = selectedOptions.map((option) => option.value);

    if (isEditMode) {
      setEditParents(selectedIds);
    } else {
      setParents(selectedIds);
    }
  };

  return (
    <div className="child-color-manager">
      <h2>Дитячі Кольори</h2>

      {/* ------------------ БЛОК СТВОРЕННЯ (якщо не редагуємо) ------------------ */}
      {!editMode && (
        <div className="create-section">
          <input
            type="text"
            className="apple-input"
            placeholder="Назва дитячого кольору..."
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          {/* ВИБІР БАТЬКІВСЬКИХ КОЛЬОРІВ */}
          <select
            multiple
            value={parents}
            onChange={(e) => handleSelectParents(e, false)}
            style={{ minHeight: "100px", width: "200px" }}
          >
            {parentColors.map((p) => (
              <option key={p._id} value={p._id}>
                {p.name}
              </option>
            ))}
          </select>

          {/* ЗАВАНТАЖЕННЯ ЗОБРАЖЕНЬ */}
          <label className="apple-btn secondary">
            Обрати зображення
            <input
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleFileChange(e, false)}
            />
          </label>

          {images.length > 0 && (
            <div style={{ margin: "1rem 0" }}>
              <p>Обрано: {images.length} зображення(-нь)</p>
            </div>
          )}

          <button className="apple-btn" onClick={handleCreateChildColor} disabled={childLoading}>
            Створити
          </button>
        </div>
      )}

      {/* ------------------ БЛОК РЕДАГУВАННЯ ------------------ */}
      {editMode && (
        <div className="edit-section">
          <input
            type="text"
            className="apple-input"
            placeholder="Нова назва дитячого кольору..."
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />

          {/* MULTIPLE SELECT для вибору (оновлення) батьківських кольорів */}
          <select
            multiple
            value={editParents}
            onChange={(e) => handleSelectParents(e, true)}
            style={{ minHeight: "100px", width: "200px" }}
          >
            {parentColors.map((p) => (
              <option key={p._id} value={p._id}>
                {p.name}
              </option>
            ))}
          </select>

          {/* Додати нові зображення */}
          <label className="apple-btn secondary">
            Додати зображення
            <input
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleFileChange(e, true)}
            />
          </label>

          {/* Вивід нових зображень, які хочемо додати */}
          {newImages.length > 0 && (
            <div style={{ margin: "1rem 0" }}>
              <p>Нові зображення:</p>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                {newImages.map((img, idx) => (
                  <div key={idx} style={{ position: "relative" }}>
                    <img
                      src={img}
                      alt="new"
                      style={{ width: "50px", height: "50px", objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      onClick={() => removeNewImage(idx)}
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        background: "rgba(255,255,255,0.8)",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      X
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            className="apple-btn"
            onClick={handleUpdateChildColor}
            disabled={childLoading}
          >
            Зберегти зміни
          </button>
          <button className="apple-btn secondary" onClick={handleCancelEdit}>
            Скасувати
          </button>
        </div>
      )}

      {/* ------------------ СПИСОК ДИТЯЧИХ КОЛЬОРІВ ------------------ */}
      <div className="child-color-list">
        {childColors.map((child) => (
          <div className="child-color-item" key={child._id}>
            <div className="child-color-info">
              <span className="child-color-name">{child.name}</span>
              <div className="child-color-parents">
                <p>Батьківські кольори:</p>
                <ul>
                  {child.parents?.length > 0 ? (
                    child.parents.map((p) => (
                      <li key={typeof p === "string" ? p : p._id}>
                        {/* 
                          Якщо бекенд популює батьківські кольори,
                          p буде об’єктом { _id, name, ... }.
                          Якщо ні — просто відобразимо _id.
                        */}
                        {typeof p === "string" ? p : p.name}
                      </li>
                    ))
                  ) : (
                    <li>Немає</li>
                  )}
                </ul>
              </div>
              <div className="child-color-images">
                {child.images?.map((imgObj, idx) => (
                  <div key={idx} style={{ position: "relative" }}>
                    <img src={imgObj.url} alt={child.name} />
                    {/* Кнопка для позначення зображення до видалення (лише в режимі редагування) */}
                    {editMode && editColorId === child._id && (
                      <button
                        type="button"
                        onClick={() => handleMarkForDelete(imgObj.public_id)}
                        style={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          background: "rgba(255,255,255,0.8)",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        X
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="actions">
              <button
                className="apple-btn secondary"
                onClick={() => handleEditMode(child)}
              >
                Редагувати
              </button>
              <button
                className="apple-btn danger"
                onClick={() => handleDeleteChildColor(child._id)}
              >
                Видалити
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Colors;
